import React, { useEffect, useRef, useState } from "react";

export default function SignaturePad({ onChange, disabled = false, isTest = true }) {
  const canvas = useRef(null);
  const stroke = useRef(null);
  const cursor = useRef({ x: 100, y: 90 });
  const [ink, setInk] = useState(false);
  const [padError, setPadError] = useState("");
  useEffect(() => {
    const node = canvas.current; const context = node?.getContext("2d");
    if (context) { context.lineWidth = 2.5; context.lineCap = "round"; context.lineJoin = "round"; context.strokeStyle = "#171717"; }
  }, []);
  function point(event) {
    const rect = canvas.current.getBoundingClientRect();
    return { x: Math.max(0, Math.min(640, (event.clientX - rect.left) * 640 / rect.width)), y: Math.max(0, Math.min(180, (event.clientY - rect.top) * 180 / rect.height)) };
  }
  function line(from, to) {
    const context = canvas.current.getContext("2d");
    if (!context) return;
    context.beginPath(); context.moveTo(from.x, from.y); context.lineTo(to.x, to.y); context.stroke(); setInk(true);
  }
  function emit() {
    try {
      const png = canvas.current.toDataURL("image/png");
      if (!png.startsWith("data:image/png;base64,") || Math.ceil((png.length - 22) * 3 / 4) > 102400) throw new Error("size");
      onChange(png); setPadError("");
    } catch (_) { onChange(null); setPadError("Unterschrift konnte nicht erfasst werden. Bitte löschen und erneut zeichnen."); }
  }
  function start(event) {
    if (disabled || (event.pointerType === "mouse" && event.button !== 0)) return;
    if (stroke.current) return;
    event.preventDefault(); canvas.current.focus();
    stroke.current = { ...point(event), id: event.pointerId, moved: false };
    canvas.current.setPointerCapture(event.pointerId);
  }
  function move(event) {
    if (disabled || !stroke.current || stroke.current.id !== event.pointerId) return;
    event.preventDefault(); const next = point(event);
    line(stroke.current, next); stroke.current = { ...next, id: event.pointerId, moved: true };
    onChange(null); // A partly changed stroke cannot submit a previously captured PNG.
  }
  function end(event) {
    if (!stroke.current || stroke.current.id !== event.pointerId) return;
    const moved = stroke.current.moved; stroke.current = null;
    if (canvas.current.hasPointerCapture(event.pointerId)) canvas.current.releasePointerCapture(event.pointerId);
    if (moved) emit();
  }
  function cancel() { if (stroke.current) { stroke.current = null; onChange(null); } }
  function keyboard(event) {
    if (disabled || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault(); const old = cursor.current;
    const next = { x: Math.max(1, Math.min(639, old.x + (event.key === "ArrowRight" ? 4 : event.key === "ArrowLeft" ? -4 : 0))), y: Math.max(1, Math.min(179, old.y + (event.key === "ArrowDown" ? 4 : event.key === "ArrowUp" ? -4 : 0))) };
    cursor.current = next;
    if (event.shiftKey) { line(old, next); emit(); }
  }
  function clear() {
    canvas.current.getContext("2d")?.clearRect(0, 0, 640, 180);
    stroke.current = null; cursor.current = { x: 100, y: 90 }; setInk(false); setPadError(""); onChange(null);
  }
  return <div className="digital-contract-signature">
    <p id="contract-signature-help">{isTest ? "TEST: " : ""}Unterschrift mit Finger oder Maus zeichnen. Tastatur: Pfeiltasten bewegen den Stift, Umschalt + Pfeiltasten zeichnen. Bei Bedarf löschen und neu beginnen.</p>
    <canvas ref={canvas} width={640} height={180} tabIndex={disabled ? -1 : 0} role="img" aria-label={isTest ? "Unterschriftsfeld für eigenen Test" : "Unterschriftsfeld"} aria-describedby="contract-signature-help" aria-disabled={disabled}
      onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={cancel} onLostPointerCapture={cancel} onKeyDown={keyboard} />
    <p aria-live="polite">{ink ? "Unterschrift gezeichnet." : "Noch keine Unterschrift gezeichnet."}</p>
    {padError && <p role="alert">{padError}</p>}
    <button type="button" disabled={disabled} onClick={clear}>Unterschrift löschen</button>
  </div>;
}
